import { Component, Prop, h, Element, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-versions-table';

/**
 * Versions table section for SignNow forms pages.
 * Displays a centered heading and subtitle followed by a three-column table
 * listing form versions with clickable title links, popularity ratings, and
 * "fillable & printable" checkmarks. Rows are passed as slotted elements with
 * data-title, data-link, and data-votes attributes.
 *
 * @product signnow
 * @source versions
 * @version 2.0.0
 * @category SignNow
 * @slot row - {required}, {multiple}, {min-items: 1}, <div data-title="..." data-link="..." data-votes="..."> - Table row entries
 * @example
 *   <sn-versions-table-type-1
 *     section-title="Find and fill out the correct W-9 form"
 *     sub-title="airSlate SignNow helps you fill in and sign documents in minutes, error-free."
 *   >
 *     <div slot="row" data-title="IRS W-9 2024" data-link="https://example.com/w9-2024" data-votes="4.8 Satisfied (346 Votes)"></div>
 *     <div slot="row" data-title="IRS W-9 2023" data-link="https://example.com/w9-2023" data-votes="4.8 Satisfied (346 Votes)"></div>
 *   </sn-versions-table-type-1>
 */
@Component({
  tag: 'sn-versions-table-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnVersionsTable {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Section heading text.
   * @content
   * @dynamic
   */
  @Prop({ attribute: 'section-title' }) sectionTitle?: string;

  /**
   * Section subtitle / description text.
   * @content
   * @dynamic
   */
  @Prop({ attribute: 'sub-title' }) subTitle?: string;

  private rows: Array<{ title: string; link: string; votes: string }> = [];

  componentWillLoad() {
    const slotted = Array.from(this.el.querySelectorAll('[slot="row"]'));
    this.rows = slotted.map(el => ({
      title: el.getAttribute('data-title') || el.getAttribute('dataTitle') || el.getAttribute('datatitle') || '',
      link: el.getAttribute('data-link') || el.getAttribute('dataLink') || el.getAttribute('datalink') || '',
      votes: el.getAttribute('data-votes') || el.getAttribute('dataVotes') || el.getAttribute('datavotes') || '',
    }));
  }

  render() {
    const sectionTitle = this.sectionTitle || this.el.getAttribute('sectiontitle') || this.el.getAttribute('section-title');
    const subTitle = this.subTitle || this.el.getAttribute('subtitle') || this.el.getAttribute('sub-title');
    const items = this.rows;
    if (items.length === 0) return null;

    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME}>
          <div class={`${COMPONENT_NAME}__container`}>
            {(sectionTitle || subTitle) && (
              <div class={`${COMPONENT_NAME}__intro`}>
                {sectionTitle && <h2 class={`${COMPONENT_NAME}__title`}>{sectionTitle}</h2>}
                {subTitle && <p class={`${COMPONENT_NAME}__subtitle`}>{subTitle}</p>}
              </div>
            )}
            <div class={`${COMPONENT_NAME}__table-wrap`}>
              <table class={`${COMPONENT_NAME}__table`}>
                <thead>
                  <tr class={`${COMPONENT_NAME}__header-row`}>
                    <th scope="col" class={`${COMPONENT_NAME}__header-cell ${COMPONENT_NAME}__header-cell--versions`}>
                      Versions
                    </th>
                    <th scope="col" class={`${COMPONENT_NAME}__header-cell ${COMPONENT_NAME}__header-cell--popularity`}>
                      Form popularity
                    </th>
                    <th scope="col" class={`${COMPONENT_NAME}__header-cell ${COMPONENT_NAME}__header-cell--fillable`}>
                      Fillable &amp; printable
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} class={{ [`${COMPONENT_NAME}__row`]: true, [`${COMPONENT_NAME}__row--highlighted`]: index % 2 !== 0 }}>
                      <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--versions`}>
                        <a href={item.link} class={`${COMPONENT_NAME}__link`}>
                          {item.title}
                        </a>
                      </td>
                      <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--popularity`}>{item.votes}</td>
                      <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--fillable`} aria-label="Available">
                        <me-icon class={`${COMPONENT_NAME}__check`} name="interface--check" aria-hidden="true" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
